import { useState, useCallback, useEffect } from 'react';

export interface UseCarouselOptions {
  totalSlides: number;
  initialSlide?: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  loop?: boolean;
  onSlideChange?: (index: number) => void;
  onComplete?: () => void;
}

export interface UseCarouselReturn {
  currentSlide: number;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  goToSlide: (index: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  skip: () => void;
}

/**
 * useCarousel - Custom hook for managing carousel state
 *
 * Provides state management for carousel navigation including
 * auto-play, looping, and callbacks.
 *
 * @example
 * const carousel = useCarousel({
 *   totalSlides: 7,
 *   onComplete: () => navigateToWizard(),
 *   autoPlay: true,
 *   autoPlayInterval: 5000,
 * });
 */
export function useCarousel({
  totalSlides,
  initialSlide = 0,
  autoPlay = false,
  autoPlayInterval = 5000,
  loop = false,
  onSlideChange,
  onComplete,
}: UseCarouselOptions): UseCarouselReturn {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === totalSlides - 1;

  const goToSlide = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSlides) {
        if (loop) {
          const wrappedIndex = ((index % totalSlides) + totalSlides) % totalSlides;
          setCurrentSlide(wrappedIndex);
          onSlideChange?.(wrappedIndex);
        }
        return;
      }
      setCurrentSlide(index);
      onSlideChange?.(index);
    },
    [totalSlides, loop, onSlideChange]
  );

  const goToNext = useCallback(() => {
    if (isLastSlide) {
      if (loop) {
        goToSlide(0);
      } else {
        onComplete?.();
      }
    } else {
      goToSlide(currentSlide + 1);
    }
  }, [currentSlide, isLastSlide, loop, goToSlide, onComplete]);

  const goToPrevious = useCallback(() => {
    if (isFirstSlide) {
      if (loop) {
        goToSlide(totalSlides - 1);
      }
    } else {
      goToSlide(currentSlide - 1);
    }
  }, [currentSlide, isFirstSlide, loop, totalSlides, goToSlide]);

  const skip = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  // Auto-play effect
  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      if (!isLastSlide || loop) {
        goToNext();
      }
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [autoPlay, autoPlayInterval, isLastSlide, loop, goToNext]);

  return {
    currentSlide,
    isFirstSlide,
    isLastSlide,
    goToSlide,
    goToNext,
    goToPrevious,
    skip,
  };
}

export default useCarousel;
